import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocalSearchParams } from "expo-router";
import { Image, Platform, StyleSheet, View } from "react-native";

import {
  AppText,
  BrandMark,
  Button,
  ButtonText,
  Card,
  ScrollScreen,
  Stack,
} from "~/components/ui";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import { workspaceLogoPublicUrl } from "~/utils/time-rounding";

type PublicQuoteItem = {
  id: string;
  description: string;
  quantity: number;
  unit_amount_cents: number;
  gst_rate: number;
};
type PublicQuote = {
  id: string;
  quote_number: string;
  title: string;
  status: string;
  issue_date: string;
  valid_until: string | null;
  currency: string;
  subtotal_cents: number;
  gst_cents: number;
  total_cents: number;
  notes: string | null;
  terms: string | null;
  purchase_order_reference: string | null;
  client_reference: string | null;
  job_starts_on: string | null;
  job_ends_on: string | null;
  quote_items: PublicQuoteItem[];
  clients: { name: string; billing_address: string | null } | null;
  workspaces: {
    name: string;
    legal_name: string | null;
    address: string | null;
    contact_email: string | null;
    phone: string | null;
    brand_accent_hex: string | null;
    logo_path: string | null;
    nzbn: string | null;
    gst_number: string | null;
  };
};

/** The public API returns the logo as a full URL; the screens build theirs from the stored path. */
function logoPathFrom(logoUrl: string | null | undefined) {
  return logoUrl?.split("/v1/logos/")[1] ?? null;
}

const DEFAULT_ACCENT = "#0f766e";

function firstParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}
function brandAccent(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return DEFAULT_ACCENT;
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  if (!/^#[0-9A-Fa-f]{6}$/.test(withHash)) return DEFAULT_ACCENT;
  return withHash;
}
function money(cents: number, currency: string) {
  return new Intl.NumberFormat("en-NZ", { style: "currency", currency }).format(
    cents / 100,
  );
}
function lineTotal(item: PublicQuoteItem) {
  const subtotal = Math.round(Number(item.quantity) * item.unit_amount_cents);
  return subtotal + Math.round((subtotal * Number(item.gst_rate)) / 100);
}

export default function PublicQuoteScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const params = useLocalSearchParams<{ token?: string }>();
  const token = firstParam(params.token) ?? "";
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["public-quote", token],
    queryFn: async () => {
      const { quote, client, workspace } = await unwrap(
        api.getPublicQuote({ path: { token } }),
      );
      return {
        ...quote,
        quote_items: quote.items,
        clients: client
          ? { name: client.name, billing_address: client.billing_address }
          : null,
        workspaces: {
          ...workspace,
          logo_path: logoPathFrom(workspace.logo_url),
        },
      } as unknown as PublicQuote;
    },
    enabled: Boolean(token),
  });
  const respond = useMutation({
    mutationFn: async (response: "accepted" | "declined") => {
      await unwrap(
        api.respondToPublicQuote({ path: { token }, body: { response } }),
      );
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["public-quote", token] }),
  });
  const quote = query.data;
  const sender =
    quote?.workspaces.legal_name || quote?.workspaces.name || "MintStint";
  const accent = brandAccent(quote?.workspaces.brand_accent_hex);
  const logoUrl = workspaceLogoPublicUrl(quote?.workspaces.logo_path);
  const canRespond = quote && ["sent", "viewed"].includes(quote.status);

  return (
    <ScrollScreen>
      <BrandMark />
      <Stack gap="md">
        <Card gap="sm">
          <AppText weight="semibold">Client quote</AppText>
          <AppText size="xs" tone="muted">
            Review scope, pricing, GST and terms from {sender}.
          </AppText>
          {query.isLoading ? (
            <AppText size="xs" tone="muted">
              Loading quote...
            </AppText>
          ) : null}
          {query.error || respond.error ? (
            <AppText size="xs" tone="danger">
              {(query.error ?? respond.error) instanceof Error
                ? (query.error ?? respond.error)?.message
                : "Unable to load this quote."}
            </AppText>
          ) : null}
          {quote ? (
            <View style={stylesheet.actions}>
              {canRespond ? (
                <>
                  <Button
                    disabled={respond.isPending}
                    onPress={() => respond.mutate("accepted")}
                  >
                    <ButtonText>Accept quote</ButtonText>
                  </Button>
                  <Button
                    variant="outline"
                    disabled={respond.isPending}
                    onPress={() => respond.mutate("declined")}
                  >
                    <ButtonText>Decline</ButtonText>
                  </Button>
                </>
              ) : null}
              <Button
                variant="outline"
                iconName="printer"
                onPress={() => {
                  if (Platform.OS === "web" && typeof window !== "undefined")
                    window.print();
                }}
              >
                <ButtonText>Print copy</ButtonText>
              </Button>
            </View>
          ) : null}
        </Card>

        {quote ? (
          <>
            <View
              style={[stylesheet.brandBanner, { borderBottomColor: accent }]}
            >
              {logoUrl ? (
                <Image
                  accessibilityIgnoresInvertColors
                  resizeMode="contain"
                  source={{ uri: logoUrl }}
                  style={stylesheet.logo}
                />
              ) : null}
              <AppText size="lg" weight="bold" style={{ color: accent }}>
                {sender}
              </AppText>
            </View>
            <View style={stylesheet.grid}>
              <Card gap="xs">
                <AppText size="xs" tone="muted">
                  Quote
                </AppText>
                <AppText size="lg" weight="bold" style={{ color: accent }}>
                  {quote.quote_number}
                </AppText>
              </Card>
              <Card gap="xs">
                <AppText size="xs" tone="muted">
                  Status
                </AppText>
                <AppText size="lg" weight="bold">
                  {quote.status}
                </AppText>
              </Card>
              <Card gap="xs">
                <AppText size="xs" tone="muted">
                  Valid until
                </AppText>
                <AppText size="lg" weight="bold">
                  {quote.valid_until ?? "No expiry"}
                </AppText>
              </Card>
              <Card gap="xs">
                <AppText size="xs" tone="muted">
                  Total
                </AppText>
                <AppText size="lg" weight="bold" style={{ color: accent }}>
                  {money(quote.total_cents, quote.currency)}
                </AppText>
              </Card>
            </View>
            <View style={stylesheet.grid}>
              <Card gap="xs">
                <AppText weight="semibold">From</AppText>
                <AppText>{sender}</AppText>
                {[
                  quote.workspaces.address,
                  quote.workspaces.contact_email,
                  quote.workspaces.phone,
                ]
                  .filter(Boolean)
                  .map((value) => (
                    <AppText key={value} size="xs">
                      {value}
                    </AppText>
                  ))}
                {quote.workspaces.nzbn ? (
                  <AppText size="xs" tone="muted">
                    NZBN: {quote.workspaces.nzbn}
                  </AppText>
                ) : null}
                {quote.workspaces.gst_number ? (
                  <AppText size="xs" tone="muted">
                    GST number: {quote.workspaces.gst_number}
                  </AppText>
                ) : null}
              </Card>
              <Card gap="xs">
                <AppText weight="semibold">Prepared for</AppText>
                <AppText>{quote.clients?.name ?? "Client"}</AppText>
                {[quote.clients?.billing_address]
                  .filter(Boolean)
                  .map((value) => (
                    <AppText key={value} size="xs">
                      {value}
                    </AppText>
                  ))}
                {quote.purchase_order_reference ? (
                  <AppText size="xs" tone="muted">
                    PO: {quote.purchase_order_reference}
                  </AppText>
                ) : null}
                {quote.client_reference ? (
                  <AppText size="xs" tone="muted">
                    Client ref: {quote.client_reference}
                  </AppText>
                ) : null}
                {quote.job_starts_on || quote.job_ends_on ? (
                  <AppText size="xs" tone="muted">
                    Job:{" "}
                    {[quote.job_starts_on, quote.job_ends_on]
                      .filter(Boolean)
                      .join(" – ")}
                  </AppText>
                ) : null}
                <AppText size="xs" tone="muted">
                  Issued {quote.issue_date}
                </AppText>
              </Card>
            </View>
            <Card gap="sm">
              <AppText size="lg" weight="bold" style={{ color: accent }}>
                {quote.title}
              </AppText>
              {quote.quote_items.map((item) => (
                <View
                  key={item.id}
                  style={[stylesheet.item, { borderColor: accent }]}
                >
                  <View style={stylesheet.itemCopy}>
                    <AppText weight="semibold">{item.description}</AppText>
                    <AppText size="xs" tone="muted">
                      {item.quantity} ×{" "}
                      {money(item.unit_amount_cents, quote.currency)} at{" "}
                      {item.gst_rate}% GST
                    </AppText>
                  </View>
                  <AppText weight="semibold">
                    {money(lineTotal(item), quote.currency)}
                  </AppText>
                </View>
              ))}
            </Card>
            <View style={stylesheet.grid}>
              <Card gap="sm">
                <AppText weight="semibold">Terms</AppText>
                <AppText size="xs" tone="muted">
                  {quote.terms ?? "No additional terms."}
                </AppText>
                {quote.notes ? (
                  <AppText size="xs" tone="muted">
                    {quote.notes}
                  </AppText>
                ) : null}
              </Card>
              <Card gap="sm">
                <View style={stylesheet.total}>
                  <AppText>Subtotal</AppText>
                  <AppText>
                    {money(quote.subtotal_cents, quote.currency)}
                  </AppText>
                </View>
                <View style={stylesheet.total}>
                  <AppText>GST</AppText>
                  <AppText>{money(quote.gst_cents, quote.currency)}</AppText>
                </View>
                <View
                  style={[
                    stylesheet.total,
                    stylesheet.grandTotal,
                    { borderTopColor: accent },
                  ]}
                >
                  <AppText weight="bold" style={{ color: accent }}>
                    Total
                  </AppText>
                  <AppText weight="bold" style={{ color: accent }}>
                    {money(quote.total_cents, quote.currency)}
                  </AppText>
                </View>
              </Card>
            </View>
          </>
        ) : null}
      </Stack>
    </ScrollScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    actions: { flexDirection: "row", flexWrap: "wrap", gap: spacing.s },
    brandBanner: {
      borderBottomWidth: 3,
      gap: spacing.s,
      paddingBottom: spacing.m,
    },
    logo: {
      height: 48,
      maxWidth: 180,
      width: 180,
    },
    grid: { gap: spacing.m },
    item: {
      alignItems: "center",
      borderColor: colors.border,
      borderRadius: radii.card,
      borderWidth: 1,
      flexDirection: "row",
      gap: spacing.m,
      justifyContent: "space-between",
      padding: spacing.m,
    },
    itemCopy: { flex: 1, gap: spacing.xs },
    total: {
      flexDirection: "row",
      justifyContent: "space-between",
      gap: spacing.m,
    },
    grandTotal: {
      borderTopWidth: 2,
      marginTop: spacing.s,
      paddingTop: spacing.s,
    },
  });
